import { Form } from "antd";
import { useEffect, useState } from "react";
import DynamicForm from "../controls/DynamicForm";
import { Reward } from "../repository/data.default";
import { RewardForm } from "./RewardFormDialog.default";
import {
  addReward,
  deleteReward,
  updateReward,
} from "../repository/data.repository";
import InModal from "../controls/InModal";
import { modalTitle } from "../controls/InModal.default";
import ModalFooter from "../controls/ModalFooter";
import ModalDeleteConfirmation from "../controls/ModalDeleteConfirmation";
import ModalErrorAlert from "../controls/ModalErrorAlert";
import { setFormFields } from "../utils/setFormFields";
import { hasFormTouched } from "../utils/hasFormChanged";
import { getProblemMessage } from "../utils/http";

export interface RewardFormDialogProps {
  readonly open?: boolean;
  readonly mode: "add" | "update" | "delete";
  readonly initialValues?: Reward;
  readonly onFinish?: () => void;
  readonly onCancel?: () => void;
}

function RewardFormDialog(props: RewardFormDialogProps) {
  const [form] = Form.useForm();
  const values = Form.useWatch<RewardForm>([], form);
  const [valid, setValid] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | undefined>(
    undefined,
  );

  useEffect(() => {
    if (props.open) {
      if (props.initialValues) {
        setFormFields(form, props.initialValues);
      } else {
        // New rewards default to published + targeted (matches the backend create defaults).
        form.setFieldsValue({ isPublished: true, isPlatformWide: false });
      }
    } else {
      reset();
    }
  }, [props.open]);

  useEffect(() => {
    form
      .validateFields({
        validateOnly: true,
      })
      .then((value) => {
        setValid(!!Object.values(value).length);
      })
      .catch(() => {
        setValid(false);
      });
  }, [form, values, props.open]);

  function onSubmit(): void {
    const { name, description, requiredPoints, isPublished, isPlatformWide } =
      values;
    setLoading(true);
    setErrorMessage(undefined);
    if (props.mode === "add") {
      addReward({
        name,
        description,
        requiredPoints,
        isPublished,
        isPlatformWide,
      })
        .then(() => {
          setLoading(false);
          props.onFinish?.();
        })
        .catch((err) => {
          setLoading(false);
          setErrorMessage(getProblemMessage(err));
        });
    } else if (props.mode === "update") {
      if (hasFormTouched(form)) {
        updateReward({
          uid: props.initialValues?.uid,
          rowVersion: props.initialValues?.rowVersion,
          name,
          description,
          requiredPoints,
          isPublished,
          isPlatformWide,
        })
          .then(() => {
            setLoading(false);
            props.onFinish?.();
          })
          .catch((err) => {
            setLoading(false);
            setErrorMessage(getProblemMessage(err));
          });
      } else {
        setLoading(false);
        props.onFinish?.();
      }
    } else if (props.mode === "delete") {
      deleteReward(props.initialValues?.uid!)
        .then(() => {
          setLoading(false);
          props.onFinish?.();
        })
        .catch((err) => {
          setLoading(false);
          setErrorMessage(getProblemMessage(err));
        });
    }
  }

  function onCancel(): void {
    props.onCancel?.();
  }

  // Methods

  function reset(): void {
    setValid(false);
    setErrorMessage(undefined);
    setLoading(false);
    form.resetFields();
  }

  return (
    <InModal
      key={props.mode}
      open={props.open}
      size={props.mode === "delete" ? "small" : "medium"}
      title={modalTitle(props.mode, "Reward", props.initialValues?.name)}
      onCancel={onCancel}
      actions={
        <ModalFooter
          mode={props.mode}
          loading={loading}
          valid={valid}
          onCancel={onCancel}
          onSubmit={onSubmit}
        />
      }
    >
      {props.mode !== "delete" && (
        <DynamicForm
          className="mb-4 w-auto"
          form={form}
          schema={{
            groups: [
              {
                controls: [
                  {
                    type: "textbox",
                    name: "name",
                    label: "Name",
                    rules: [
                      {
                        required: true,
                        message: "Name is required",
                      },
                    ],
                  },
                  {
                    type: "textbox",
                    name: "description",
                    label: "Description",
                    rules: [
                      {
                        required: true,
                        message: "Description is required",
                      },
                    ],
                  },
                  {
                    type: "numberbox",
                    name: "requiredPoints",
                    label: "Required points",
                    step: "1",
                    rules: [
                      {
                        required: true,
                        message: "Reward points is required",
                      },
                      {
                        validator: (_, value) => {
                          const numValue = Number(value);
                          if (!Number.isInteger(numValue)) {
                            return Promise.reject(
                              new Error("Only integer values are allowed"),
                            );
                          } else {
                            return Promise.resolve();
                          }
                        },
                      },
                    ],
                  },
                  {
                    type: "togglebox",
                    name: "isPublished",
                    label: "Published",
                  },
                  {
                    type: "togglebox",
                    name: "isPlatformWide",
                    label: "Platform-wide (all companies)",
                  },
                ],
              },
            ],
          }}
        />
      )}
      {props.mode === "delete" && (
        <ModalDeleteConfirmation
          entityLabel="reward"
          entityName={props.initialValues?.name}
        />
      )}
      <ModalErrorAlert message={errorMessage} />
    </InModal>
  );
}

export default RewardFormDialog;
